import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  FormControl,
  makeStyles,
  MenuItem,
  Select,
  TextField,
  Tooltip
} from "@material-ui/core";
import useNotifier from "@saleor/hooks/useNotifier";
import { maybe } from "@saleor/misc";
import { MotherOrderList_orders_edges_node } from "@saleor/motherOrders/types/MotherOrderList";
import {
  isExpressDelivery,
  isStandardDeliveryNewRTS
} from "@saleor/motherOrders/utils/handlers";
import { SmopOrderStatusEnum } from "@saleor/types/globalTypes";
import React from "react";

import { useOrderAssignRider } from "../../../mutations";
import { OpsLateDeliveryList } from "../../OrderAssignRiderDialog/initialData";

interface OpsAdminReasonForLateProps {
  order: MotherOrderList_orders_edges_node;
  refetchOrders;
}

const useStyles = makeStyles(
  theme => ({
    cancelReason: {
      WebkitBoxOrient: "vertical",
      WebkitLineClamp: 2,
      width: "100%",
      [theme.breakpoints.down("sm")]: {
        display: "unset"
      }
    },
    formControl: {
      margin: theme.spacing(1),
      minWidth: 200
    },
    menu: {
      textOverflow: "ellipsis",
      width: 300
    }
  }),

  { name: "CancelReasonField" }
);

const OpsAdminReasonForLate: React.FC<OpsAdminReasonForLateProps> = props => {
  const { order, refetchOrders } = props;
  const classes = useStyles(props);
  const notify = useNotifier();

  const [open, setOpen] = React.useState(false);
  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSubmitReason = () => {
    if (otherReasonText.length < 250) {
      assignRider({
        variables: {
          courier: order.courier,
          id: order?.id,
          lateDispatchReason: otherReasonText
        }
      });
    } else {
      notify({
        status: "error",
        text: `Characters limit exceeded`
      });
      handleClose();
    }
  };

  const [assignRider] = useOrderAssignRider({
    onCompleted: data => {
      if (data.orderAssignRider.orderErrors.length === 0) {
        notify({
          status: "success",
          text: `Ops Admin Reason Updated.`
        });
        refetchOrders();
        handleClose();
      } else {
        notify({
          status: "error",
          text: `${data.orderAssignRider.orderErrors[0].message}`
        });
        handleClose();
      }
    },
    onError: errors => {
      const [error] = errors.graphQLErrors;
      notify({
        status: "error",
        text: error?.message
      });
    }
  });

  const [adminReason, setAdminReason] = React.useState(
    order?.lateDispatchReason ? order?.lateDispatchReason : ""
  );
  const [otherReasonText, setOtherReasonText] = React.useState("");
  const [toolTipDisplayText, setToolTipDisplayText] = React.useState("");

  const handleChange = event => {
    setAdminReason(event.target.value);
    if (event.target.value !== "OTHERS") {
      assignRider({
        variables: {
          courier: order.courier,
          id: order?.id,
          lateDispatchReason: event.target.value
        }
      });
    }
  };

  const getLateDispatchReason = reason => {
    const lateDispatchReason = OpsLateDeliveryList.find(r => r.value === reason)
      ?.label;

    return lateDispatchReason || reason || "--";
  };

  React.useEffect(() => {
    if (adminReason === "OTHERS") {
      handleClickOpen();
    }
  }, [adminReason]);

  React.useEffect(() => {
    setAdminReason(order?.lateDispatchReason ? order?.lateDispatchReason : "");

    const found = OpsLateDeliveryList.find(
      element => element.value === adminReason
    );

    if (found) {
      if (found.value === "OTHERS") {
        setToolTipDisplayText(order?.lateDispatchReason);
      } else {
        setToolTipDisplayText(found?.label);
      }
    } else {
      setToolTipDisplayText(order?.lateDispatchReason);
    }
  }, [order]);

  return (
    <>
      {maybe(
        () =>
          isStandardDeliveryNewRTS(
            order?.shippingMethod?.deliveryType,
            order?.smopStatus
          ) ||
          (isExpressDelivery(order?.shippingMethod?.deliveryType) &&
            order?.smopStatus !== SmopOrderStatusEnum.CANCELED)
      ) ? (
        <div onClick={e => e.stopPropagation()}>
          <Tooltip placement="top" title={toolTipDisplayText} interactive>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                width: "100%"
              }}
            >
              <div className={classes.cancelReason}>
                <FormControl className={classes.formControl}>
                  <Select
                    style={{ backgroundColor: "transparent", width: 300 }}
                    multiline
                    labelId="demo-simple-select-standard-label"
                    id="opsAdminReasonSelect"
                    value={adminReason}
                    onChange={handleChange}
                    MenuProps={{ className: classes.menu }}
                  >
                    {OpsLateDeliveryList.find(
                      element => element.value === adminReason
                    ) ? null : (
                      <MenuItem
                        disabled
                        key={adminReason}
                        value={adminReason}
                        style={{ color: "#000000" }}
                      >
                        {adminReason}
                      </MenuItem>
                    )}

                    {OpsLateDeliveryList.map(reason => (
                      <MenuItem
                        key={reason.value}
                        value={reason.value}
                        style={{ color: "#000000" }}
                      >
                        {reason.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </div>
          </Tooltip>
          <Dialog
            open={open}
            onClose={handleClose}
            aria-labelledby="form-dialog-title"
          >
            <DialogContent>
              <DialogContentText>
                Enter Ops Admin Reason for Late Delivery
              </DialogContentText>
              <TextField
                onChange={event => {
                  setOtherReasonText(event.target.value);
                }}
                autoFocus
                margin="dense"
                multiline
                rows={3}
                id="OpsAdminReasonTxt"
                fullWidth
              />
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setAdminReason(
                    order?.lateDispatchReason ? order?.lateDispatchReason : ""
                  );
                  handleClose();
                }}
                color="primary"
              >
                Cancel
              </Button>
              <Button onClick={handleSubmitReason} color="primary">
                Submit
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      ) : (
        <div>{getLateDispatchReason(order?.lateDispatchReason)}</div>
      )}
    </>
  );
};

export default OpsAdminReasonForLate;
